import { useEffect, useState } from "react";
import { StateMessage } from "@/components/StateMessage";
import { ApiError, demoApi, problemMessage } from "@/api/client";
import type { Shift } from "@/api/types";
import "./MyShifts.css";

type LoadState = "loading" | "ready" | "error" | "offline";

/**
 * ef-040-4 — the worker's own upcoming shifts, `GET /api/demo/schedule/mine`. The route
 * itself decides the window (`ScheduleEndpoints.MyScheduleWindowDays` — 30 days) and this
 * screen never asks for a wider one. Read-only, D4: shift CRUD is the owner's board
 * (ef-040-3, a different screen) — no button or link is ever rendered on a row here.
 *
 * SAME OFFLINE DISCIPLINE AS THE JOB-CODES EFFECT IN `TimeClock.tsx` — never attempted
 * while `navigator.onLine` is false, retried on the same `'online'` signal
 * `drainTriggers.ts` already uses elsewhere. A worker who cannot reach it yet still sees
 * the punch clock; this section only ever fails on its own.
 * @ui-check: this must never fetch while `navigator.onLine` is false — a stray request
 * here is exactly the console-error class `offline-punch.spec.ts`'s own AC9 gate exists
 * to catch, applied to a fetch that file does not itself exercise.
 */
export function MyShifts() {
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [state, setState] = useState<LoadState>("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // ef-054 gates 63/68 — React 18 StrictMode double-invokes this effect on mount
    // (mount → cleanup → mount again). Without an AbortController the first
    // invocation's `fetch()` has no owner once `cancelled` flips: it stays on the wire,
    // and if a network drop lands while it is still in flight, Chromium logs "Failed to
    // load resource: net::ERR_INTERNET_DISCONNECTED" for it regardless of the `.catch`
    // below — the browser reports the network layer, not app state. Aborting on cleanup
    // matches the AbortController pattern already used elsewhere (Access.tsx,
    // Books*.tsx, Reports*.tsx) — this effect and TimeOffRequests.tsx's own were the
    // two outliers missing it.
    const controller = new AbortController();
    function load(): void {
      // Known-offline never even attempts the request (same reasoning as the job-codes
      // effect in TimeClock.tsx) — but unlike that effect, this list is the whole of what
      // the section has to show, so staying silent would leave a worker staring at
      // "Loading your shifts…" forever. AC8 (offline-punch.spec.ts) reloads /time with no
      // network and expects the screen to render something honest, not a stuck spinner
      // and not an alert — an offline reload is not an error the worker must act on.
      if (!navigator.onLine) {
        setState("offline");
        return;
      }
      demoApi
        .getMySchedule(controller.signal)
        .then((rows) => {
          if (cancelled) return;
          setShifts(rows);
          setState("ready");
          setError(null);
        })
        .catch((err) => {
          if (cancelled) return;
          // A fetch that never reached the API (no network, or the reverse proxy
          // couldn't reach the app — ApiError.neverReachedTheServer's own remarks) reads
          // the same as knowing offline up front: quiet, not a role="alert" the worker
          // has to dismiss. Only a real answer FROM the server — the app ran and said
          // no — is a genuine error.
          if (err instanceof ApiError && err.neverReachedTheServer) {
            setState("offline");
            setError(null);
            return;
          }
          setState("error");
          setError(problemMessage(err, "Could not load your shifts."));
        });
    }
    load();
    window.addEventListener("online", load);
    return () => {
      cancelled = true;
      controller.abort();
      window.removeEventListener("online", load);
    };
  }, []);

  const dataState =
    state === "error"
      ? "error"
      : state === "offline"
        ? "offline"
        : state === "loading"
          ? "loading"
          : shifts.length === 0
            ? "empty"
            : "ready";

  return (
    <section className="my-shifts" aria-labelledby="my-shifts-heading" data-state={dataState}>
      <h2 id="my-shifts-heading" className="time__heading time__heading--sub">
        My shifts
      </h2>

      {dataState === "error" ? (
        <StateMessage tone="error" title={error ?? "Could not load your shifts."} />
      ) : dataState === "offline" ? (
        // Deliberately NOT a StateMessage tone="error" — role="status", never
        // role="alert" (ui-qa.md's own alert budget; offline-punch.spec.ts's AC8 gates
        // on zero alerts after an offline reload). Ground truth is the data-state
        // attribute itself, same convention as TimeClock.tsx's own OfflineTag.
        <p className="my-shifts__offline" role="status" data-state="offline">
          Offline — will refresh when back online.
        </p>
      ) : dataState === "loading" ? (
        <StateMessage tone="loading" title="Loading your shifts…" />
      ) : dataState === "empty" ? (
        <StateMessage
          tone="empty"
          title="Nothing scheduled in the next 30 days."
          detail="Shifts your employer publishes will show up here."
        />
      ) : (
        <ul className="my-shifts__list" aria-labelledby="my-shifts-heading">
          {shifts.map((shift) => (
            <li
              key={shift.id}
              className="my-shifts__row"
              // ui-breadcrumbs §1 — ground truth for a tester: `data-shift-id` is what
              // worker-schedule.spec.ts's own `shiftRow` locator finds a row BY, and
              // `data-status` mirrors the row's own `published`/`cancelled` badge rather
              // than requiring a screen reader to read the badge text to know which.
              data-shift-id={shift.id}
              data-status={shift.status}
            >
              <span className="my-shifts__when">{formatShiftRange(shift.scheduledStart, shift.scheduledEnd)}</span>
              {shift.status === "cancelled" && (
                <span className="my-shifts__badge my-shifts__badge--cancelled">Cancelled</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function formatShiftRange(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const day = start.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  const startTime = start.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  const endTime = end.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${day}, ${startTime} – ${endTime}`;
}
